import styled from "styled-components";
import type { ReactNode } from "react";
import type { ColumnDef, RowData, VisibilityState } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { Layers, Pencil, RotateCcw } from "lucide-react";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import { CatalogOrigin } from "@KassaroAB/shared/types/enums/CatalogOrigin";
import { BarcodeType } from "@KassaroAB/shared/types/enums/BarcodeType";
import { Country } from "@KassaroAB/shared/types/enums/Country";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { RestProduct, RestUpdateProduct } from "@KassaroAB/shared/types/product";
import type {
  BarcodeFormatId,
  BookkeepingAccountId,
  CourseId,
  PrepStationId,
  ProductGroupId,
  ProductId,
  ProductVariantId,
  UnitId,
} from "@KassaroAB/shared/types/ids";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import type { Money } from "@KassaroAB/shared/types/money";
import {
  ProductVariantOverlayFieldScheme,
  type ProductOverlayField,
  type RestProductOverlayListItem,
} from "@KassaroAB/shared/types/productOverlay";
import type { StockPolicyAssignment } from "@KassaroAB/shared/types/stockPolicy";
import type { RestBookkeepingAccount } from "@KassaroAB/shared/types/bookkeepingAccount";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import type { RestStore } from "@KassaroAB/shared/types/store";
import type { ExtraProductField } from "@KassaroAB/shared/types/extraProductField";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatMoney } from "../../../utils/formatMoney";
import { formatDate } from "../../../utils/dateUtils";
import {
  isProductBarcodeDirty,
  isProductExtraFieldDirty,
  isProductFieldDirty,
  isVariantFieldDirty,
  type EditableVariantField,
} from "../../../utils/productEditDiff";
import { formatMoneyAmountInputValue } from "../../../utils/parseMoneyAmountInput";
import {
  getProductGroupForRow,
  getProductGroupLockedValue,
  isProductGroupFieldLocked,
} from "../../../utils/productGroupLock";
import { CatalogOriginChip } from "../../../components/CatalogOriginChip";
import { Tooltip } from "../../../components/Tooltip";
import {
  CellInput,
  CellSelect,
  MoneyTableCell,
  OverlayCell,
  OverlayCellBody,
  OverrideMarker,
  OverrideResetButton,
  ReadOnlyFieldset,
} from "./MoneyTableCell";
import type { ProductTableStock } from "./useProductTableStock";

export const TABLE_PRODUCT_TYPES = [
  ProductType.PRODUCT,
  ProductType.SERVICE,
] as const;

const VAT_OPTIONS = [25, 12, 6, 0] as const;

export interface ProductColumnLabel {
  id: string;
  labelKey?: string;
  label?: string;
  features?: Features[];
}

export type StockStatus = "out" | "low" | null;

export type ChainProductSaveTarget = "chain" | "store";

export interface ProductOverlayMeta {
  saveTarget: ChainProductSaveTarget;
  overlays: ReadonlyMap<ProductId, RestProductOverlayListItem>;
  allowedFields: ReadonlySet<ProductOverlayField>;
  reset: (
    productId: ProductId,
    field: ProductOverlayField,
    variantId?: ProductVariantId,
  ) => void;
  isResetting: boolean;
}

export interface ProductTableMeta {
  edits: Record<string, RestUpdateProduct>;
  setEdit: (
    id: ProductId,
    product: RestProduct,
    changes: RestUpdateProduct,
  ) => void;
  groups: { id: ProductGroupId; name: string }[];
  productGroupsById: ReadonlyMap<ProductGroupId, RestProductGroup>;
  units: { id: UnitId; name: string }[];
  courses: { id: CourseId; name: string }[];
  prepStations: { id: PrepStationId; name: string }[];
  bookkeepingAccounts: RestBookkeepingAccount[];
  country: Country;
  bookkeepingDefaultAccounts: RestStore["bookkeepingDefaultAccounts"];
  currency: Currency;
  onEditProduct: (product: RestProduct) => void;
  showCatalogOrigin: boolean;
  overlay: ProductOverlayMeta | null;
  isExpanded: (id: ProductId) => boolean;
  toggleExpanded: (id: ProductId) => void;
  stock: ProductTableStock;
  stockStatus: (
    quantity: number | null,
    assignment: StockPolicyAssignment | null | undefined,
  ) => StockStatus;
  t: TFunction;
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    productMeta?: ProductTableMeta;
  }
}

const RETAIL = [Features.BARCODE_SCANNING, Features.SIMPLE_STOCK];
const BARCODE = [Features.BARCODE_SCANNING];
const PREP = [Features.PREP_TICKETS];
const TAKEAWAY = [Features.TAKEOUT_PRICE];

const STATIC_COLUMN_LABELS: ProductColumnLabel[] = [
  { id: "name", labelKey: "productsView.tableName" },
  { id: "articleNumber", labelKey: "productsView.tableArticleNumber", features: RETAIL },
  { id: "unitPrice", labelKey: "productsView.tableUnitPrice" },
  { id: "vat", labelKey: "productsView.tableVat" },
  { id: "type", labelKey: "productsView.tableType" },
  { id: "group", labelKey: "productsView.tableGroup", features: [Features.GROUPS] },
  { id: "stockCount", labelKey: "productsView.tableStock", features: [Features.SIMPLE_STOCK] },
  { id: "unit", labelKey: "productForm.unit" },
  { id: "openPrice", labelKey: "productForm.openPrice" },
  { id: "bookkeepingAccount", labelKey: "productForm.bookkeepingAccountId" },
  { id: "bookkeepingCostcenter", labelKey: "productsView.tableBookkeepingCostcenter" },
  { id: "information", labelKey: "productForm.information", features: [Features.PRODUCT_INFO_BOX] },
  { id: "purchasePrice", labelKey: "productForm.purchasePrice", features: [Features.PURCHASE_PRICE] },
  { id: "barcodeCode", labelKey: "productForm.barcodeCode", features: BARCODE },
  { id: "barcodeType", labelKey: "productForm.barcodeType", features: BARCODE },
  { id: "barcodeFormat", labelKey: "productForm.barcodeFormatId", features: BARCODE },
  {
    id: "manufacturerArticleNumber",
    labelKey: "productForm.manufacturerArticleNumber",
    features: RETAIL,
  },
  { id: "manufacturerBarcode", labelKey: "productForm.manufacturerBarcode", features: RETAIL },
  { id: "prepStation", labelKey: "productForm.prepStation", features: PREP },
  { id: "course", labelKey: "productForm.course", features: PREP },
  { id: "requireReference", labelKey: "productForm.requireReference" },
  { id: "takeawayVAT", labelKey: "productForm.takeawayVat", features: TAKEAWAY },
  {
    id: "takeawayBookkeepingAccount",
    labelKey: "productForm.takeawayBookkeepingAccount",
    features: TAKEAWAY,
  },
  { id: "takeawayPrice", labelKey: "productForm.takeawayPrice", features: TAKEAWAY },
  { id: "description", labelKey: "productForm.description" },
  { id: "updatedAt", labelKey: "productsView.tableUpdated" },
];

const UNLABELLED_COLUMN_IDS = new Set(["select", "actions"]);

export type HasProductFeature = (features?: Features[]) => boolean;

export const buildProductColumnLabels = (
  extraFields: ExtraProductField[] | null | undefined,
  hasFeature: HasProductFeature,
): ProductColumnLabel[] => {
  const extraLabels = (extraFields ?? []).map((field) => ({
    id: `extra:${field.key}`,
    label: field.name,
    features: [Features.EXTRA_PRODUCT_FIELDS],
  }));
  return [...STATIC_COLUMN_LABELS, ...extraLabels].filter((column) =>
    hasFeature(column.features),
  );
};

export const DEFAULT_VISIBLE_PRODUCT_COLUMN_IDS = new Set([
  "name",
  "unitPrice",
  "articleNumber",
  "barcodeCode",
  "group",
  "vat",
  "stockCount",
  "bookkeepingAccount",
  "unit",
]);

export const buildDefaultColumnVisibility = (
  columnLabels: ProductColumnLabel[],
): VisibilityState => {
  const state: VisibilityState = {};
  columnLabels.forEach((column) => {
    if (!DEFAULT_VISIBLE_PRODUCT_COLUMN_IDS.has(column.id)) {
      state[column.id] = false;
    }
  });
  return state;
};

const NameCell = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${spacing.x1}px;
  min-width: 0;
`;

const NameField = styled.div`
  flex: 1;
  min-width: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const VariantIndent = styled.div`
  flex-shrink: 0;
  width: ${spacing.x2}px;
`;

const VariantToggle = styled.button<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 1px 6px;
  border: 1px solid
    ${({ $expanded }) => ($expanded ? colors.primary : colors.border)};
  border-radius: 10px;
  background-color: ${({ $expanded }) =>
    $expanded ? colors.infoBackground : colors.background};
  color: ${({ $expanded }) => ($expanded ? colors.primary : colors.textMuted)};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 1px;
  }
`;

const VariantNameInput = styled(CellInput)`
  &::placeholder {
    color: ${colors.text};
  }
`;

const StockInput = styled(CellInput)<{ $status: StockStatus }>`
  width: 72px;
  -moz-appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${({ $status }) =>
    $status &&
    `
    color: ${$status === "out" ? colors.dangerText : colors.warningText};
    font-weight: 600;
  `}

  ${({ $status, $dirty }) =>
    $status === "out" &&
    !$dirty &&
    `background-color: ${colors.dangerBackground};`}
`;

const ReadOnlyValue = styled.div`
  padding: ${spacing.x05}px ${spacing.x1}px;
  font-size: ${fontSizes.medium}px;
  color: ${colors.textMuted};
`;

export const rowVariants = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
): ProductVariant[] => edits?.variants ?? product.variants ?? [];

const setVariantEdit = (
  meta: ProductTableMeta,
  product: RestProduct,
  variantId: ProductVariantId,
  changes: Partial<ProductVariant>,
) =>
  meta.setEdit(product.id, product, {
    variants: rowVariants(product, meta.edits[product.id]).map((variant) =>
      variant.id === variantId ? { ...variant, ...changes } : variant,
    ),
  });

const parseStockInput = (value: string): number | null =>
  value === "" ? null : Number(value);

const variantStock = (
  meta: ProductTableMeta,
  product: RestProduct,
  variant: ProductVariant,
): number | null =>
  meta.stock.stockLocationId
    ? meta.stock.getQuantity(product.id, variant.id)
    : (variant.stockCount ?? null);

const formatVariantStockSum = (
  meta: ProductTableMeta,
  product: RestProduct,
  variants: ProductVariant[],
): string => {
  const quantities = variants
    .map((variant) => variantStock(meta, product, variant))
    .filter((quantity): quantity is number => quantity !== null);
  if (quantities.length === 0) {
    return "–";
  }
  const sum = quantities.reduce((total, quantity) => total + quantity, 0);
  return String(Math.round(sum * 1000) / 1000);
};

const locationStockInput = (
  meta: ProductTableMeta,
  productId: ProductId,
  variantId: ProductVariantId | null,
) => {
  const quantity = meta.stock.getQuantity(productId, variantId);
  return (
  <StockInput
    type="number"
    disabled={meta.stock.isLoading(productId)}
    $dirty={meta.stock.isDirty(productId, variantId)}
    $status={meta.stockStatus(
      quantity,
      meta.stock.getStockPolicy(productId, variantId),
    )}
    value={quantity ?? ""}
    onChange={(event) =>
      meta.stock.setQuantity(
        productId,
        variantId,
        parseStockInput(event.target.value),
      )
    }
  />
  );
};

const variantBarcode = (variant: ProductVariant) => ({
  code: variant.barcode?.code ?? "",
  type: variant.barcode?.type ?? BarcodeType.EAN_13,
  format: variant.barcode?.format ?? null,
});

const OVERLAY_FIELD_BY_COLUMN: Partial<Record<string, ProductOverlayField>> = {
  name: "name",
  unitPrice: "unitPrice",
  takeawayPrice: "takeawayPrice",
  prepStation: "prepStationId",
  bookkeepingAccount: "bookkeepingAccountId",
};

const VARIANT_OVERLAY_FIELDS = new Set<ProductOverlayField>(
  ProductVariantOverlayFieldScheme.options,
);

export const isOverlayFieldLocked = (
  overlay: Pick<ProductOverlayMeta, "saveTarget" | "allowedFields"> | null,
  origin: CatalogOrigin | undefined,
  field: string | null,
): boolean =>
  overlay?.saveTarget === "store" &&
  origin === CatalogOrigin.CHAIN &&
  !(field !== null && overlay.allowedFields.has(field as ProductOverlayField));

export const restrictOverlayChanges = (
  overlay: Pick<ProductOverlayMeta, "saveTarget" | "allowedFields"> | null,
  product: RestProduct,
  changes: RestUpdateProduct,
): RestUpdateProduct =>
  Object.fromEntries(
    Object.entries(changes).filter(([key]) =>
      key === "variants"
        ? ProductVariantOverlayFieldScheme.options.some(
            (field) => !isOverlayFieldLocked(overlay, product.origin, field),
          )
        : !isOverlayFieldLocked(overlay, product.origin, key),
    ),
  );

const isChainCellReadOnly = (
  meta: ProductTableMeta,
  product: RestProduct,
  columnId: string,
  isVariant: boolean,
): boolean => {
  if (columnId === "stockCount" && meta.stock.stockLocationId) {
    return false;
  }
  const field = OVERLAY_FIELD_BY_COLUMN[columnId] ?? null;
  return isOverlayFieldLocked(
    meta.overlay,
    product.origin,
    isVariant && field && !VARIANT_OVERLAY_FIELDS.has(field) ? null : field,
  );
};

const formatOverlayValue = (
  meta: ProductTableMeta,
  field: ProductOverlayField,
  value: unknown,
  fallbackPrice: Money | null | undefined,
): string => {
  switch (field) {
    case "unitPrice":
    case "takeawayPrice":
      return formatMoney((value as Money | null | undefined) ?? fallbackPrice);
    case "name":
      return (value as string | undefined) || "–";
    case "prepStationId":
      return (
        meta.prepStations.find((station) => station.id === value)?.name ?? "–"
      );
    case "bookkeepingAccountId":
      return value
        ? formatBookkeepingAccountLabel(meta, value as BookkeepingAccountId, "")
        : meta.t("productsView.bookkeepingDefault");
  }
};

type OverlayValues = Partial<Record<ProductOverlayField, unknown>>;

export const overrideTooltip = (
  meta: ProductTableMeta,
  product: RestProduct,
  field: ProductOverlayField,
  variantId?: ProductVariantId,
): string | null => {
  const overlay = meta.overlay;
  const item = overlay?.overlays.get(product.id);
  if (!overlay || !item || product.origin !== CatalogOrigin.CHAIN) {
    return null;
  }
  const stored: OverlayValues | undefined = variantId
    ? item.overlay.variants?.[variantId]
    : item.overlay;
  if (!stored || !(field in stored)) {
    return null;
  }
  const inStore = overlay.saveTarget === "store";
  const source: OverlayValues | undefined = !inStore
    ? stored
    : variantId
      ? item.chainValues.variants?.find((variant) => variant.id === variantId)
      : item.chainValues;
  const fallbackPrice = variantId
    ? ((!inStore && field in item.overlay
        ? item.overlay[field]
        : item.chainValues[field]) as Money | null | undefined)
    : undefined;
  const value = formatOverlayValue(meta, field, source?.[field], fallbackPrice);
  return meta.t(
    inStore ? "productsView.overlayStoreValue" : "productsView.overlayChainValue",
    { value },
  );
};

export const overrideMarker = (
  meta: ProductTableMeta,
  product: RestProduct,
  field: ProductOverlayField,
  variantId?: ProductVariantId,
  onReset?: () => void,
): ReactNode => {
  const tooltip = overrideTooltip(meta, product, field, variantId);
  const overlay = meta.overlay;
  if (!tooltip || !overlay) {
    return null;
  }
  return (
    <>
      <Tooltip content={tooltip}>
        <OverrideMarker role="img" aria-label={tooltip} />
      </Tooltip>
      {overlay.saveTarget === "store" && (
        <OverrideResetButton
          type="button"
          disabled={overlay.isResetting}
          title={meta.t("productsView.overlayReset")}
          aria-label={meta.t("productsView.overlayReset")}
          onClick={onReset ?? (() => overlay.reset(product.id, field, variantId))}
        >
          <RotateCcw size={12} />
        </OverrideResetButton>
      )}
    </>
  );
};

const decorateChainCell = (
  meta: ProductTableMeta,
  product: RestProduct,
  columnId: string,
  content: ReactNode,
  variantId?: ProductVariantId,
): ReactNode => {
  if (!meta.overlay || product.origin !== CatalogOrigin.CHAIN) {
    return content;
  }
  const field = OVERLAY_FIELD_BY_COLUMN[columnId];
  const marker =
    field && (!variantId || VARIANT_OVERLAY_FIELDS.has(field))
      ? overrideMarker(meta, product, field, variantId)
      : null;
  const body =
    isChainCellReadOnly(meta, product, columnId, !!variantId) &&
    (variantId || columnId !== "name") ? (
      <ReadOnlyFieldset disabled>{content}</ReadOnlyFieldset>
    ) : (
      content
    );
  if (!marker) {
    return body;
  }
  return (
    <OverlayCell>
      <OverlayCellBody>{body}</OverlayCellBody>
      {marker}
    </OverlayCell>
  );
};

const withChainCell = (
  column: ColumnDef<RestProduct, unknown>,
): ColumnDef<RestProduct, unknown> => {
  const cell = column.cell;
  const columnId = column.id;
  if (
    typeof cell !== "function" ||
    !columnId ||
    UNLABELLED_COLUMN_IDS.has(columnId)
  ) {
    return column;
  }
  return {
    ...column,
    cell: (context) => {
      const meta = context.table.options.meta?.productMeta;
      const content = cell(context);
      return meta
        ? decorateChainCell(meta, context.row.original, columnId, content)
        : content;
    },
  };
};

const variantPlaceholderName = (
  product: RestProduct,
  variant: ProductVariant,
): string =>
  Object.values(variant.extraFields ?? {})
    .filter((value) => value.trim() !== "")
    .join(" / ") || product.name;

export const renderVariantCell = (
  columnId: string,
  product: RestProduct,
  variant: ProductVariant,
  meta: ProductTableMeta | undefined,
): ReactNode =>
  meta
    ? decorateChainCell(
        meta,
        product,
        columnId,
        renderVariantCellContent(columnId, product, variant, meta),
        variant.id,
      )
    : null;

const renderVariantCellContent = (
  columnId: string,
  product: RestProduct,
  variant: ProductVariant,
  meta: ProductTableMeta,
): ReactNode => {
  const edits = meta.edits[product.id];
  const dirty = (field: EditableVariantField) =>
    isVariantFieldDirty(product, edits, variant.id, field);
  const patch = (changes: Partial<ProductVariant>) =>
    setVariantEdit(meta, product, variant.id, changes);
  const textInput = (
    field: "articleNumber" | "manufacturerBarcode",
  ): ReactNode => (
    <CellInput
      $dirty={dirty(field)}
      value={variant[field] ?? ""}
      onChange={(event) => patch({ [field]: event.target.value || null })}
    />
  );
  const moneyInput = (
    field: "unitPrice" | "purchasePrice" | "takeawayPrice",
    inherited: RestProduct["unitPrice"] | null | undefined,
  ): ReactNode => (
    <MoneyTableCell
      dirty={dirty(field)}
      amountMinor={variant[field]?.amount}
      placeholder={
        inherited ? formatMoneyAmountInputValue(inherited.amount) : undefined
      }
      allowEmpty
      onCommit={(minor) =>
        patch({
          [field]:
            minor === null ? null : { amount: minor, currency: meta.currency },
        })
      }
    />
  );
  const barcode = variantBarcode(variant);

  if (columnId.startsWith("extra:")) {
    const key = columnId.slice("extra:".length);
    const extraFields = variant.extraFields ?? {};
    return (
      <CellInput
        $dirty={
          (extraFields[key] ?? "") !==
          (product.variants?.find((item) => item.id === variant.id)
            ?.extraFields?.[key] ?? "")
        }
        value={extraFields[key] ?? ""}
        onChange={(event) =>
          patch({ extraFields: { ...extraFields, [key]: event.target.value } })
        }
      />
    );
  }

  switch (columnId) {
    case "name":
      return (
        <NameCell>
          <VariantIndent />
          <NameField>
            <VariantNameInput
              $dirty={dirty("name")}
              value={variant.name ?? ""}
              placeholder={variantPlaceholderName(product, variant)}
              onChange={(event) => patch({ name: event.target.value || null })}
            />
          </NameField>
        </NameCell>
      );
    case "articleNumber":
    case "manufacturerBarcode":
      return textInput(columnId);
    case "unitPrice":
      return moneyInput("unitPrice", product.unitPrice);
    case "purchasePrice":
      return moneyInput("purchasePrice", product.purchasePrice);
    case "takeawayPrice":
      return moneyInput("takeawayPrice", product.takeawayPrice);
    case "barcodeCode":
      return (
        <CellInput
          $dirty={dirty("barcode")}
          value={barcode.code}
          onChange={(event) =>
            patch({
              barcode: event.target.value
                ? { ...barcode, code: event.target.value }
                : undefined,
            })
          }
        />
      );
    case "barcodeType":
      return (
        <CellSelect
          disabled={!barcode.code}
          $dirty={dirty("barcode")}
          value={barcode.type}
          onChange={(event) =>
            patch({
              barcode: { ...barcode, type: event.target.value as BarcodeType },
            })
          }
        >
          {Object.values(BarcodeType).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </CellSelect>
      );
    case "barcodeFormat":
      return (
        <CellInput
          disabled={!barcode.code}
          $dirty={dirty("barcode")}
          value={barcode.format ?? ""}
          onChange={(event) =>
            patch({
              barcode: {
                ...barcode,
                format: event.target.value
                  ? (event.target.value as BarcodeFormatId)
                  : null,
              },
            })
          }
        />
      );
    case "stockCount":
      return meta.stock.stockLocationId ? (
        locationStockInput(meta, product.id, variant.id)
      ) : (
        <StockInput
          type="number"
          $dirty={dirty("stockCount")}
          $status={meta.stockStatus(
            variant.stockCount ?? null,
            variant.stockPolicy,
          )}
          value={variant.stockCount ?? ""}
          onChange={(event) =>
            patch({ stockCount: parseStockInput(event.target.value) })
          }
        />
      );
    default:
      return null;
  }
};

const getRowProductGroup = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  meta: ProductTableMeta,
) => getProductGroupForRow(product, edits, meta.productGroupsById);

const isRowFieldLocked = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  meta: ProductTableMeta,
  field: keyof RestProductGroup,
) => isProductGroupFieldLocked(field, getRowProductGroup(product, edits, meta));

const bookkeepingTypeForDefaults = (type: ProductType): ProductType =>
  type === ProductType.SERVICE ? ProductType.SERVICE : ProductType.PRODUCT;

const getDefaultBookkeepingAccountNumber = (
  meta: ProductTableMeta,
  vat: number,
  type: ProductType,
): string => {
  const merged = getEffectiveBookkeepingMapping(
    meta.country,
    meta.bookkeepingDefaultAccounts,
  );
  const vatAccount = merged.vatRateMappings.find((mapping) => mapping.vatRate === vat);
  if (!vatAccount) return "3001";
  const key =
    bookkeepingTypeForDefaults(type) === ProductType.PRODUCT
      ? "product"
      : "service";
  return vatAccount.salesAccount[key];
};

const formatBookkeepingAccountLabel = (
  meta: ProductTableMeta,
  accountId: BookkeepingAccountId | null | undefined,
  defaultNumber: string,
): string => {
  if (!accountId) {
    return `${meta.t("productsView.bookkeepingDefault")}: ${defaultNumber}`;
  }
  const account = meta.bookkeepingAccounts.find((item) => item.id === accountId);
  if (!account) return accountId;
  return account.name
    ? `${account.name}: ${account.accountNumber}`
    : account.accountNumber;
};

const mergeBarcode = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
) => ({
  code: edits?.barcode?.code ?? product.barcode?.code ?? "",
  type: edits?.barcode?.type ?? product.barcode?.type ?? BarcodeType.EAN_13,
  format: edits?.barcode?.format ?? product.barcode?.format ?? null,
});

const mergeExtraFields = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
): Record<string, string> => ({
  ...(product.extraFields ?? {}),
  ...(edits?.extraFields ?? {}),
});

const createExtraFieldColumns = (
  extraFields: ExtraProductField[],
): ColumnDef<RestProduct, unknown>[] =>
  extraFields.map((field) => ({
    id: `extra:${field.key}`,
    size: 160,
    enableSorting: false,
    header: () => field.name,
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) {
        return row.original.extraFields?.[field.key] ?? "–";
      }
      const edits = meta.edits[row.original.id];
      const merged = mergeExtraFields(row.original, edits);
      const value = merged[field.key] ?? "";
      return (
        <CellInput
          $dirty={isProductExtraFieldDirty(row.original, edits, field.key)}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              extraFields: { ...merged, [field.key]: event.target.value },
            })
          }
        />
      );
    },
  }));

export const createProductColumns = (
  extraFields: ExtraProductField[] | null | undefined,
  columnLabels: ProductColumnLabel[],
  stockByLocation: boolean,
): ColumnDef<RestProduct, unknown>[] => {
  const shownIds = new Set(columnLabels.map((column) => column.id));
  return buildAllProductColumns(extraFields)
    .filter(
      (column) =>
        column.id !== undefined &&
        (UNLABELLED_COLUMN_IDS.has(column.id) || shownIds.has(column.id)),
    )
    .map((column) =>
      withChainCell(
        stockByLocation && column.id === "stockCount"
          ? { ...column, enableSorting: false }
          : column,
      ),
    );
};

const buildAllProductColumns = (
  extraFields: ExtraProductField[] | null | undefined,
): ColumnDef<RestProduct, unknown>[] => [
  {
    id: "select",
    size: 44,
    enableSorting: false,
    meta: { align: "center", stickyLeft: true },
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
  },
  {
    id: "name",
    accessorKey: "name",
    size: 240,
    meta: { stickyLeft: true },
    header: ({ table }) => table.options.meta?.productMeta?.t("productsView.tableName"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.name;
      const edits = meta.edits[row.original.id];
      const value = edits?.name ?? row.original.name;
      const variantCount = rowVariants(row.original, edits).length;
      const expanded = meta.isExpanded(row.original.id);
      return (
        <NameCell>
          {meta.showCatalogOrigin && (
            <CatalogOriginChip origin={row.original.origin} />
          )}
          <NameField>
            <CellInput
              $dirty={isProductFieldDirty(row.original, edits, "name")}
              disabled={isChainCellReadOnly(meta, row.original, "name", false)}
              value={value}
              onChange={(event) =>
                meta.setEdit(row.original.id, row.original, {
                  name: event.target.value,
                })
              }
            />
          </NameField>
          {variantCount > 0 && (
            <VariantToggle
              type="button"
              $expanded={expanded}
              aria-expanded={expanded}
              title={meta.t(
                expanded
                  ? "productsView.hideVariants"
                  : "productsView.showVariants",
                { count: variantCount },
              )}
              onClick={() => meta.toggleExpanded(row.original.id)}
            >
              <Layers size={12} />
              {variantCount}
            </VariantToggle>
          )}
        </NameCell>
      );
    },
  },
  {
    id: "articleNumber",
    accessorKey: "articleNumber",
    size: 160,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableArticleNumber"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.articleNumber ?? "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.articleNumber ?? row.original.articleNumber ?? "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "articleNumber")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              articleNumber: event.target.value,
            })
          }
        />
      );
    },
  },
  {
    id: "unitPrice",
    accessorFn: (row) => row.unitPrice.amount,
    size: 130,
    meta: { align: "right" },
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableUnitPrice"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return formatMoney(row.original.unitPrice);
      const edits = meta.edits[row.original.id];
      const amount = edits?.unitPrice?.amount ?? row.original.unitPrice.amount;
      const variantAmounts = rowVariants(row.original, edits).map(
        (variant) => variant.unitPrice?.amount ?? amount,
      );
      const minAmount = Math.min(...variantAmounts);
      const maxAmount = Math.max(...variantAmounts);
      const hasPriceRange =
        variantAmounts.length > 0 &&
        (minAmount !== maxAmount || minAmount !== amount);
      return (
        <MoneyTableCell
          idleText={
            hasPriceRange
              ? minAmount === maxAmount
                ? formatMoneyAmountInputValue(minAmount)
                : `${formatMoneyAmountInputValue(minAmount)}–${formatMoneyAmountInputValue(maxAmount)}`
              : undefined
          }
          title={
            hasPriceRange
              ? meta.t("productsView.basePrice", {
                  price: formatMoneyAmountInputValue(amount),
                })
              : undefined
          }
          dirty={isProductFieldDirty(row.original, edits, "unitPrice")}
          amountMinor={amount}
          onCommit={(minor) => {
            if (minor === null) {
              return;
            }
            meta.setEdit(row.original.id, row.original, {
              unitPrice: {
                amount: minor,
                currency: row.original.unitPrice.currency,
              },
            });
          }}
        />
      );
    },
  },
  {
    id: "vat",
    accessorKey: "VAT",
    size: 90,
    header: ({ table }) => table.options.meta?.productMeta?.t("productsView.tableVat"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.VAT;
      const edits = meta.edits[row.original.id];
      const locked = isRowFieldLocked(row.original, edits, meta, "VAT");
      const value = locked
        ? ((getProductGroupLockedValue(
            "VAT",
            getRowProductGroup(row.original, edits, meta),
          ) as number | null) ?? row.original.VAT)
        : (edits?.VAT ?? row.original.VAT);
      return (
        <CellSelect
          disabled={locked}
          $dirty={!locked && isProductFieldDirty(row.original, edits, "VAT")}
          value={String(value)}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              VAT: Number(event.target.value),
            })
          }
        >
          {VAT_OPTIONS.map((vat) => (
            <option key={vat} value={String(vat)}>
              {vat}%
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "type",
    accessorKey: "type",
    size: 150,
    header: ({ table }) => table.options.meta?.productMeta?.t("productsView.tableType"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.type;
      const edits = meta.edits[row.original.id];
      const locked = isRowFieldLocked(row.original, edits, meta, "type");
      const value = locked
        ? ((getProductGroupLockedValue(
            "type",
            getRowProductGroup(row.original, edits, meta),
          ) as ProductType | null) ?? row.original.type)
        : (edits?.type ?? row.original.type);
      return (
        <CellSelect
          disabled={locked}
          $dirty={!locked && isProductFieldDirty(row.original, edits, "type")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              type: event.target.value as ProductType,
            })
          }
        >
          {!TABLE_PRODUCT_TYPES.includes(
            value as (typeof TABLE_PRODUCT_TYPES)[number],
          ) && (
            <option value={value}>{meta.t(`productForm.type${value}`)}</option>
          )}
          {TABLE_PRODUCT_TYPES.map((type) => (
            <option key={type} value={type}>
              {meta.t(`productForm.type${type}`)}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "group",
    size: 190,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableGroup"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.groupId ?? row.original.groupId ?? "";
      return (
        <CellSelect
          $dirty={isProductFieldDirty(row.original, edits, "groupId")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              groupId: event.target.value
                ? (event.target.value as ProductGroupId)
                : null,
            })
          }
        >
          <option value="">–</option>
          {meta.groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "stockCount",
    accessorKey: "stockCount",
    size: 110,
    meta: { align: "right" },
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableStock"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.stockCount ?? "–";
      const edits = meta.edits[row.original.id];
      const variants = rowVariants(row.original, edits);
      if (variants.length > 0) {
        return (
          <ReadOnlyValue>
            {formatVariantStockSum(meta, row.original, variants)}
          </ReadOnlyValue>
        );
      }
      if (meta.stock.stockLocationId) {
        return locationStockInput(meta, row.original.id, null);
      }
      const value = edits?.stockCount ?? row.original.stockCount ?? "";
      return (
        <StockInput
          $dirty={isProductFieldDirty(row.original, edits, "stockCount")}
          $status={meta.stockStatus(
            value === "" ? null : value,
            row.original.stockPolicy,
          )}
          type="number"
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              stockCount:
                event.target.value === "" ? null : Number(event.target.value),
            })
          }
        />
      );
    },
  },
  {
    id: "unit",
    size: 150,
    enableSorting: false,
    header: ({ table }) => table.options.meta?.productMeta?.t("productForm.unit"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const locked = isRowFieldLocked(row.original, edits, meta, "unitId");
      const value = locked
        ? ((getProductGroupLockedValue(
            "unitId",
            getRowProductGroup(row.original, edits, meta),
          ) as UnitId | null) ?? "")
        : (edits?.unitId ?? row.original.unitId ?? "");
      return (
        <CellSelect
          disabled={locked}
          $dirty={!locked && isProductFieldDirty(row.original, edits, "unitId")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              unitId: event.target.value ? (event.target.value as UnitId) : null,
            })
          }
        >
          <option value="">–</option>
          {meta.units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "openPrice",
    size: 90,
    enableSorting: false,
    meta: { align: "center" },
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.openPrice"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.openPrice ? "✓" : "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.openPrice ?? row.original.openPrice;
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, { openPrice: event.target.checked })
          }
        />
      );
    },
  },
  {
    id: "bookkeepingAccount",
    size: 220,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.bookkeepingAccountId"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const group = getRowProductGroup(row.original, edits, meta);
      const typeLocked = isProductGroupFieldLocked("type", group);
      const vatLocked = isProductGroupFieldLocked("VAT", group);
      const accountLocked = isProductGroupFieldLocked("bookkeepingAccountId", group);
      const type = typeLocked
        ? ((getProductGroupLockedValue("type", group) as ProductType | null) ??
          row.original.type)
        : (edits?.type ?? row.original.type);
      const vat = vatLocked
        ? ((getProductGroupLockedValue("VAT", group) as number | null) ??
          row.original.VAT)
        : (edits?.VAT ?? row.original.VAT);
      const accountId = accountLocked
        ? ((getProductGroupLockedValue(
            "bookkeepingAccountId",
            group,
          ) as BookkeepingAccountId | null) ?? null)
        : (edits?.bookkeepingAccountId ?? row.original.bookkeepingAccountId);
      const defaultNumber = getDefaultBookkeepingAccountNumber(meta, vat, type);
      const value = accountId ?? "";
      return (
        <CellSelect
          disabled={accountLocked}
          $dirty={
            !accountLocked &&
            isProductFieldDirty(row.original, edits, "bookkeepingAccountId")
          }
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              bookkeepingAccountId: event.target.value
                ? (event.target.value as BookkeepingAccountId)
                : null,
            })
          }
        >
          <option value="">
            {formatBookkeepingAccountLabel(meta, null, defaultNumber)}
          </option>
          {meta.bookkeepingAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {formatBookkeepingAccountLabel(meta, account.id, defaultNumber)}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "bookkeepingCostcenter",
    size: 150,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableBookkeepingCostcenter"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.bookkeepingCostcenter ?? "–";
      const edits = meta.edits[row.original.id];
      const value =
        edits?.bookkeepingCostcenter ?? row.original.bookkeepingCostcenter ?? "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "bookkeepingCostcenter")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              bookkeepingCostcenter: event.target.value,
            })
          }
        />
      );
    },
  },
  {
    id: "information",
    size: 200,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.information"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.information ?? "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.information ?? row.original.information ?? "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "information")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, { information: event.target.value })
          }
        />
      );
    },
  },
  {
    id: "purchasePrice",
    size: 130,
    meta: { align: "right" },
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.purchasePrice"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      const baseAmount = row.original.purchasePrice?.amount;
      if (!meta) {
        return baseAmount != null ? formatMoney(row.original.purchasePrice!) : "–";
      }
      const edits = meta.edits[row.original.id];
      const amount = edits?.purchasePrice?.amount ?? baseAmount;
      return (
        <MoneyTableCell
          dirty={isProductFieldDirty(row.original, edits, "purchasePrice")}
          amountMinor={amount}
          allowEmpty
          onCommit={(minor) =>
            meta.setEdit(row.original.id, row.original, {
              purchasePrice:
                minor === null
                  ? null
                  : { amount: minor, currency: meta.currency },
            })
          }
        />
      );
    },
  },
  {
    id: "barcodeCode",
    accessorFn: (row) => row.barcode?.code ?? "",
    size: 150,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.barcodeCode"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.barcode?.code ?? "–";
      const edits = meta.edits[row.original.id];
      const barcode = mergeBarcode(row.original, edits);
      return (
        <CellInput
          $dirty={isProductBarcodeDirty(row.original, edits)}
          value={barcode.code}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, { barcode: { ...barcode, code: event.target.value } })
          }
        />
      );
    },
  },
  {
    id: "barcodeType",
    size: 140,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.barcodeType"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.barcode?.type ?? "–";
      const edits = meta.edits[row.original.id];
      const barcode = mergeBarcode(row.original, edits);
      return (
        <CellSelect
          $dirty={isProductBarcodeDirty(row.original, edits)}
          value={barcode.type ?? BarcodeType.EAN_13}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              barcode: {
                ...barcode,
                type: event.target.value as BarcodeType,
              },
            })
          }
        >
          {Object.values(BarcodeType).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "barcodeFormat",
    size: 140,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.barcodeFormatId"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.barcode?.format ?? "–";
      const edits = meta.edits[row.original.id];
      const barcode = mergeBarcode(row.original, edits);
      return (
        <CellInput
          $dirty={isProductBarcodeDirty(row.original, edits)}
          value={barcode.format ?? ""}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              barcode: {
                ...barcode,
                format: event.target.value
                  ? (event.target.value as BarcodeFormatId)
                  : null,
              },
            })
          }
        />
      );
    },
  },
  {
    id: "manufacturerArticleNumber",
    size: 180,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.manufacturerArticleNumber"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.manufacturerArticleNumber ?? "–";
      const edits = meta.edits[row.original.id];
      const value =
        edits?.manufacturerArticleNumber ??
        row.original.manufacturerArticleNumber ??
        "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "manufacturerArticleNumber")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              manufacturerArticleNumber: event.target.value,
            })
          }
        />
      );
    },
  },
  {
    id: "manufacturerBarcode",
    size: 160,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.manufacturerBarcode"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.manufacturerBarcode ?? "–";
      const edits = meta.edits[row.original.id];
      const value =
        edits?.manufacturerBarcode ?? row.original.manufacturerBarcode ?? "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "manufacturerBarcode")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              manufacturerBarcode: event.target.value,
            })
          }
        />
      );
    },
  },
  ...createExtraFieldColumns(extraFields ?? []),
  {
    id: "prepStation",
    size: 170,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.prepStation"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.prepStationId ?? row.original.prepStationId ?? "";
      return (
        <CellSelect
          $dirty={isProductFieldDirty(row.original, edits, "prepStationId")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              prepStationId: event.target.value
                ? (event.target.value as PrepStationId)
                : null,
            })
          }
        >
          <option value="">–</option>
          {meta.prepStations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.name}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "course",
    size: 150,
    enableSorting: false,
    header: ({ table }) => table.options.meta?.productMeta?.t("productForm.course"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const locked = isRowFieldLocked(row.original, edits, meta, "courseId");
      const value = locked
        ? ((getProductGroupLockedValue(
            "courseId",
            getRowProductGroup(row.original, edits, meta),
          ) as CourseId | null) ?? "")
        : (edits?.courseId ?? row.original.courseId ?? "");
      return (
        <CellSelect
          disabled={locked}
          $dirty={!locked && isProductFieldDirty(row.original, edits, "courseId")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              courseId: event.target.value ? (event.target.value as CourseId) : null,
            })
          }
        >
          <option value="">–</option>
          {meta.courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "requireReference",
    size: 110,
    enableSorting: false,
    meta: { align: "center" },
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.requireReference"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.requireReference ? "✓" : "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.requireReference ?? row.original.requireReference;
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              requireReference: event.target.checked,
            })
          }
        />
      );
    },
  },
  {
    id: "takeawayVAT",
    size: 130,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.takeawayVat"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.takeawayVAT ?? "–";
      const edits = meta.edits[row.original.id];
      const locked = isRowFieldLocked(row.original, edits, meta, "takeawayVAT");
      const raw = locked
        ? (getProductGroupLockedValue(
            "takeawayVAT",
            getRowProductGroup(row.original, edits, meta),
          ) as number | null | undefined)
        : (edits?.takeawayVAT ?? row.original.takeawayVAT);
      const value = raw === null || raw === undefined ? "" : String(raw);
      return (
        <CellSelect
          disabled={locked}
          $dirty={!locked && isProductFieldDirty(row.original, edits, "takeawayVAT")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              takeawayVAT:
                event.target.value === "" ? null : Number(event.target.value),
            })
          }
        >
          <option value="">{meta.t("productForm.notSet")}</option>
          {VAT_OPTIONS.map((vat) => (
            <option key={vat} value={String(vat)}>
              {vat}%
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "takeawayBookkeepingAccount",
    size: 220,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.takeawayBookkeepingAccount"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return "–";
      const edits = meta.edits[row.original.id];
      const group = getRowProductGroup(row.original, edits, meta);
      const typeLocked = isProductGroupFieldLocked("type", group);
      const vatLocked = isProductGroupFieldLocked("VAT", group);
      const takeawayVatLocked = isProductGroupFieldLocked("takeawayVAT", group);
      const accountLocked = isProductGroupFieldLocked(
        "takeawayBookkeepingAccountId",
        group,
      );
      const type = typeLocked
        ? ((getProductGroupLockedValue("type", group) as ProductType | null) ??
          row.original.type)
        : (edits?.type ?? row.original.type);
      const vat = vatLocked
        ? ((getProductGroupLockedValue("VAT", group) as number | null) ??
          row.original.VAT)
        : (edits?.VAT ?? row.original.VAT);
      const takeawayVat = takeawayVatLocked
        ? (getProductGroupLockedValue("takeawayVAT", group) as
            | number
            | null
            | undefined)
        : (edits?.takeawayVAT ?? row.original.takeawayVAT);
      const accountId = accountLocked
        ? ((getProductGroupLockedValue(
            "takeawayBookkeepingAccountId",
            group,
          ) as BookkeepingAccountId | null) ?? null)
        : (edits?.takeawayBookkeepingAccountId ??
          row.original.takeawayBookkeepingAccountId);
      const defaultVat =
        takeawayVat === null || takeawayVat === undefined ? vat : takeawayVat;
      const defaultNumber = getDefaultBookkeepingAccountNumber(
        meta,
        defaultVat,
        type,
      );
      const value = accountId ?? "";
      return (
        <CellSelect
          disabled={accountLocked}
          $dirty={
            !accountLocked &&
            isProductFieldDirty(
              row.original,
              edits,
              "takeawayBookkeepingAccountId",
            )
          }
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, {
              takeawayBookkeepingAccountId: event.target.value
                ? (event.target.value as BookkeepingAccountId)
                : null,
            })
          }
        >
          <option value="">
            {formatBookkeepingAccountLabel(meta, null, defaultNumber)}
          </option>
          {meta.bookkeepingAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {formatBookkeepingAccountLabel(meta, account.id, defaultNumber)}
            </option>
          ))}
        </CellSelect>
      );
    },
  },
  {
    id: "takeawayPrice",
    size: 130,
    meta: { align: "right" },
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.takeawayPrice"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      const baseAmount = row.original.takeawayPrice?.amount;
      if (!meta) {
        return baseAmount != null ? formatMoney(row.original.takeawayPrice!) : "–";
      }
      const edits = meta.edits[row.original.id];
      const amount = edits?.takeawayPrice?.amount ?? baseAmount;
      return (
        <MoneyTableCell
          dirty={isProductFieldDirty(row.original, edits, "takeawayPrice")}
          amountMinor={amount}
          allowEmpty
          onCommit={(minor) =>
            meta.setEdit(row.original.id, row.original, {
              takeawayPrice:
                minor === null
                  ? null
                  : { amount: minor, currency: meta.currency },
            })
          }
        />
      );
    },
  },
  {
    id: "description",
    size: 200,
    enableSorting: false,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productForm.description"),
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return row.original.description ?? "–";
      const edits = meta.edits[row.original.id];
      const value = edits?.description ?? row.original.description ?? "";
      return (
        <CellInput
          $dirty={isProductFieldDirty(row.original, edits, "description")}
          value={value}
          onChange={(event) =>
            meta.setEdit(row.original.id, row.original, { description: event.target.value })
          }
        />
      );
    },
  },
  {
    id: "updatedAt",
    accessorKey: "updatedAt",
    size: 120,
    header: ({ table }) =>
      table.options.meta?.productMeta?.t("productsView.tableUpdated"),
    cell: ({ row }) => formatDate(row.original.updatedAt),
  },
  {
    id: "actions",
    size: 60,
    enableSorting: false,
    meta: { align: "center", stickyRight: true },
    header: () => "",
    cell: ({ row, table }) => {
      const meta = table.options.meta?.productMeta;
      if (!meta) return null;
      return (
        <IconButton
          type="button"
          onClick={() => meta.onEditProduct(row.original)}
        >
          <Pencil size={16} />
        </IconButton>
      );
    },
  },
];
